<script setup>
// "ANEXAR O ARQUIVO" NO AVISO DE ARQUIVO QUE FICOU NO CELULAR (25/09/2026).
//
// Na troca para a API oficial a Meta passou só o aviso de que houve um arquivo;
// o arquivo mesmo está no celular do escritório. Quem o pegar lá anexa aqui e ele
// entra no lugar do aviso, na mesma posição e data da conversa.
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMessageContext } from '../../provider.js';

defineProps({
  // mensagem que a Meta não repassou: dá para copiar o TEXTO do celular também
  comTexto: { type: Boolean, default: false },
});

const { id, conversationId } = useMessageContext();
const store = useStore();
const { t } = useI18n();

const enviando = ref(false);
const copiando = ref(false);
const texto = ref('');
const campo = ref(null);

async function mandar(dados, pronto) {
  enviando.value = true;
  const conta = store.getters.getCurrentAccountId;
  try {
    const { data } = await window.axios.post(
      `/api/v1/accounts/${conta}/conversations/${conversationId.value}/messages/${id.value}/arquivo_do_celular`,
      dados
    );
    // o tempo real também traz; isto só adianta a tela de quem anexou
    Promise.resolve(store.dispatch('updateMessage', data)).catch(() => {});
    useAlert(t(pronto));
    copiando.value = false;
  } catch (e) {
    useAlert(
      e?.response?.data?.error || t('CONVERSATION.ARQUIVO_DO_CELULAR.ERRO')
    );
  } finally {
    enviando.value = false;
  }
}

function anexar(evento) {
  const arquivo = evento.target.files?.[0];
  evento.target.value = '';
  if (!arquivo) return;
  const dados = new FormData();
  dados.append('arquivo', arquivo);
  mandar(dados, 'CONVERSATION.ARQUIVO_DO_CELULAR.PRONTO');
}

async function abrirCopia() {
  copiando.value = true;
  await nextTick();
  campo.value?.focus();
}

function copiar() {
  if (!texto.value.trim()) return;
  mandar({ texto: texto.value }, 'CONVERSATION.ARQUIVO_DO_CELULAR.COPIADO');
}
</script>

<template>
  <form v-if="copiando" class="arq-cel__copia" @submit.prevent="copiar">
    <textarea
      ref="campo"
      v-model="texto"
      name="texto-do-celular"
      rows="3"
      maxlength="4000"
      class="arq-cel__texto reset-base"
      :placeholder="t('CONVERSATION.ARQUIVO_DO_CELULAR.TEXTO_PH')"
      :aria-label="t('CONVERSATION.ARQUIVO_DO_CELULAR.COPIAR')"
      @keydown.meta.enter.prevent="copiar"
      @keydown.ctrl.enter.prevent="copiar"
    />
    <div class="arq-cel__botoes">
      <button type="button" class="arq-cel__sair" @click="copiando = false">
        {{ t('CONVERSATION.ARQUIVO_DO_CELULAR.CANCELAR') }}
      </button>
      <button
        type="submit"
        class="arq-cel"
        :disabled="enviando || !texto.trim()"
      >
        <i
          :class="enviando ? 'i-ph-circle-notch advup-gira' : 'i-ph-check-bold'"
          aria-hidden="true"
        />
        {{ t('CONVERSATION.ARQUIVO_DO_CELULAR.SALVAR_TEXTO') }}
      </button>
    </div>
  </form>
  <div v-else class="arq-cel__acoes">
    <button v-if="comTexto" type="button" class="arq-cel" @click="abrirCopia">
      <i class="i-ph-copy-bold" aria-hidden="true" />
      <span>{{ t('CONVERSATION.ARQUIVO_DO_CELULAR.COPIAR') }}</span>
    </button>
    <label class="arq-cel" :class="{ 'arq-cel--enviando': enviando }">
      <i
        :class="
          enviando ? 'i-ph-circle-notch advup-gira' : 'i-ph-paperclip-bold'
        "
        aria-hidden="true"
      />
      <span>
        {{
          enviando
            ? t('CONVERSATION.ARQUIVO_DO_CELULAR.ENVIANDO')
            : t('CONVERSATION.ARQUIVO_DO_CELULAR.ANEXAR')
        }}
      </span>
      <input
        type="file"
        class="sr-only"
        :disabled="enviando"
        :aria-label="t('CONVERSATION.ARQUIVO_DO_CELULAR.ANEXAR')"
        @change="anexar"
      />
    </label>
  </div>
</template>

<style scoped>
.arq-cel__acoes {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-2);
}

.arq-cel__copia {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
}

.arq-cel__texto {
  width: 100%;
  min-width: 240px;
  padding: var(--esp-2) var(--esp-3);
  border: 1px solid rgb(var(--blue-7));
  border-radius: var(--r-3);
  background: rgb(var(--solid-1));
  font-size: var(--txt-corpo);
  line-height: var(--linha-normal);
  color: rgb(var(--slate-12));
  resize: vertical;
}

.arq-cel__texto:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgb(var(--blue-9) / 18%);
}

.arq-cel__botoes {
  display: flex;
  justify-content: flex-end;
  gap: var(--esp-2);
}

.arq-cel__sair {
  padding: var(--esp-1) var(--esp-3);
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
}

.arq-cel {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  align-self: flex-start;
  min-height: 32px;
  padding: var(--esp-1) var(--esp-3);
  border: 1px dashed rgb(var(--blue-7));
  border-radius: var(--r-3);
  background: rgb(var(--blue-2));
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: rgb(var(--blue-11));
  cursor: pointer;
  transition:
    background var(--mov-1) var(--mov-curva),
    border-color var(--mov-1) var(--mov-curva);
}

.arq-cel:hover {
  border-style: solid;
  background: rgb(var(--blue-3));
}

.arq-cel:focus-within {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 2px;
}

.arq-cel--enviando {
  cursor: progress;
  opacity: 0.7;
}
</style>
